/**
 * Hyperlocal design tokens — TypeScript projection.
 * Governed by docs/DESIGN_LANGUAGE.md. CSS mirror: src/styles/tokens.css.
 *
 * TS modules (and every MapLibre paint expression) read constants from here;
 * injected stylesheets read the CSS custom properties. Never hand-copy a
 * value between files — import it.
 */

export const COLOR = {
  bg: "#0A0A0B",
  bgApp: "#0F0F0F",
  surface: "#141417",
  surfaceGlass: "rgba(20, 21, 24, 0.94)",
  hairline: "rgba(255, 255, 255, 0.06)",
  hairline2: "rgba(255, 255, 255, 0.10)",

  ink: "#F4F2EE",
  ink2: "#B8B4AC",
  ink3: "#8A8680",

  gold: "#FFD37E",
  gold2: "#FFE6B0",
  goldA08: "rgba(255, 211, 126, 0.08)",
  goldA12: "rgba(255, 211, 126, 0.12)",
  goldA16: "rgba(255, 211, 126, 0.16)",
  goldA22: "rgba(255, 211, 126, 0.22)",
  goldA35: "rgba(255, 211, 126, 0.35)",
  goldA55: "rgba(255, 211, 126, 0.55)",

  live: "#FF4A4A",
  green: "#7CDCA8",
  blue: "#9FCFE0",
  trustOk: "#9FCFB4",
  trustDisputed: "#FFB4A8",
} as const;

/**
 * Verdict triad — semantically load-bearing. Same verdict, same colour,
 * everywhere. verdict-card, area-snapshot and compare-card import THIS —
 * the era of hand-synced copies ("matches verdict-card's LEVEL_COLORS")
 * is over.
 */
export const LEVEL_COLORS: Record<"green" | "amber" | "red", string> = {
  green: "#1d9e75",
  amber: "#d9a400",
  red: "#c04040",
};

/** Verdict pill tail copy — one voice for the verdict across surfaces. */
export const LEVEL_TAILS: Record<keyof typeof LEVEL_COLORS, string> = {
  green: "worth considering",
  amber: "know the trade-offs",
  red: "think twice",
};

/** Compare/lens mode accents (was duplicated verdict-card.ts ⇄ compare-card.ts). */
export const MODE_ACCENT: Record<string, string> = {
  family: "#6FB37A",
  investor: "#FFD37E",
  renter: "#9FCFE0",
  any: "#B8B4AC",
};

/**
 * Signal-category palette — one copy (was hand-synced across incident-cards,
 * radar-pulse-markers and chrome-shell). Bubble markers, tap cards and rail
 * dots must agree on what a category looks like.
 */
export const SUBTYPE_COLOR: Record<string, string> = {
  crime_violent: "#FF4A4A",
  crime_property: "#FF7A33",
  crime_other: "#FF9933",
  traffic_major: "#FFA64A",
  traffic_routine: "#FFC880",
  construction_major: "#FFB94A",
  construction_routine: "#FFD37E",
  weather_severe: "#5ACBFF",
  weather_routine: "#7AB3FF",
  civic: "#5A9CFF",
  events: "#B355FF",
  aqi: "#5FE898",
  other: "#9AA0A8",
};

export const DUR = {
  fast: 140,
  base: 200,
  slow: 280,
  reveal: 320,
} as const;

export const EASE = {
  out: "cubic-bezier(0.2, 0.8, 0.2, 1)",
  spring: "cubic-bezier(0.2, 0.9, 0.3, 1.05)",
} as const;

/**
 * Z ladder — claim a rung, never invent a number.
 * map 0 · map overlays · rails · cards/snapshot · sheets · toasts ·
 * home chip · loader.
 */
export const Z = {
  mapOverlay: 100,
  rail: 1200,
  card: 1300,
  sheet: 1400,
  toast: 1500,
  homeChip: 2500,
  loader: 9999,
} as const;

/** The one mobile breakpoint — never retype the media query. */
export const BP_MOBILE = "(max-width: 720px), (max-height: 500px)";

/** Camera grammar: one select move, one framing move (ms). */
export const CAMERA = {
  select: 800,
  frame: 900,
} as const;

export const FONT = {
  display: '"Space Grotesk", system-ui, sans-serif',
  body: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
} as const;
